import React, { useContext, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FieldArgs, AutocompleteField } from '../types';
import InputLabel from '../../InputLabel/InputLabel';
import { useFormError } from '../utils';
import FormError from '../../FormError/FormError';
import Autocomplete from '../../Autocomplete/Autocomplete';
import get from 'lodash/get';
import FormControl from '../../FormControl/FormControl';

export type AutocompleteAdapterProps = FieldArgs<AutocompleteField>;

type CreatableContextType = {
  onCreate: (option: Value) => void;
  onUpdate: (option: Value, newOption: Value) => void;
  onRemove: (option: Value) => void;
};

const CreatableOptionsContext = React.createContext<CreatableContextType>({
  onCreate: () => {},
  onUpdate: () => {},
  onRemove: () => {},
});

export const CreatableOptionsProvider = (
  props: React.PropsWithChildren<CreatableContextType>,
) => {
  const { children, ...rest } = props;

  return (
    <CreatableOptionsContext.Provider value={rest}>
      {children}
    </CreatableOptionsContext.Provider>
  );
};

const useCreatableContext = () => useContext(CreatableOptionsContext);

type Value = {
  label: string;
  value: string | number;
};

const defaultSort = (a: Value, b: Value) => {
  const nameA = a.label.toUpperCase();
  const nameB = b.label.toUpperCase();

  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }

  // names must be equal
  return 0;
};

const SelectCreatableAdapter = (props: AutocompleteAdapterProps) => {
  const {
    label,
    name,
    rules,
    options: defaultOptions = [],
    multiple = false,
  } = props;
  const {
    control,
    getValues,
    formState: { errors, defaultValues },
  } = useFormContext();
  const error = useFormError(get(errors, name));
  const defaultValue = get(defaultValues, name) || (multiple ? [] : null);
  const currentValue = get(getValues(), name, []);
  const [options, setOptions] = useState(defaultOptions);
  const creatableContext = useCreatableContext();

  const onUpdate = (option: Value, newOption: Value) => {
    setOptions((prev) => {
      return prev.map((item) => {
        if (item.value === option.value) {
          return {
            label: newOption.label,
            value: newOption.value || newOption.label,
          };
        }

        return item;
      });
    });

    creatableContext.onUpdate(option, newOption);
  };

  const onRemove = (option: Value) => {
    setOptions((prev) => {
      return prev.filter((item) => item.value !== option.value);
    });

    creatableContext.onRemove(option);
  };

  const isOptionEqualToValue = (option: Value, value: Value) =>
    option.value === value.value;

  return (
    <FormControl>
      <InputLabel>{label}</InputLabel>
      <Controller
        render={({ field: { value, onChange, disabled } }) => (
          <Autocomplete
            value={value || defaultValue}
            onChange={(event, value) => {
              onChange(Array.isArray(value) ? value.sort(defaultSort) : value);
            }}
            multiple={multiple}
            options={options}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionKey={(option) => option.value}
            creatable
            onCreate={(value) => {
              const newOption = {
                label: value,
                value,
              };

              if (options.find((opt) => isOptionEqualToValue(opt, newOption))) {
                return;
              }

              setOptions((prev) => [...prev, newOption].sort(defaultSort));
              onChange([...currentValue, newOption].sort(defaultSort));

              creatableContext.onCreate(newOption);
            }}
            onOptionUpdate={onUpdate}
            onOptionRemove={onRemove}
          />
        )}
        control={control}
        name={name}
        rules={rules}
      />
      <FormError error={error} />
    </FormControl>
  );
};

export default SelectCreatableAdapter;
